import {Icon, Text, Toggle} from '@albert-io/atomic';
import React, {useCallback, useMemo} from 'react';

import {callAction} from 'client/framework';

import questionEditorActions from '../QuestionEditor/QuestionEditor.actions';
import questionEditorStore from '../QuestionEditor/QuestionEditor.store';

import {useQuestionEditorV2Store} from '../QuestionEditorV2Store';

import {GenerateTranslationsButton} from './GenerateTranslationsButton';

export const TranslationSection = () => {
  const translationsHaveChanges = useQuestionEditorV2Store((state) => state.hasChanges());
  const question = questionEditorStore.getQuestion();
  const questionSet = questionEditorStore.getQuestionSet();
  const subject = questionSet?.getAuthoringSubject();

  const subjectQuestionTranslationDisabled =
    subject != null && subject.isTranslateQuestions?.() === false;
  const subjectSupplementTranslationDisabled =
    subject != null && subject.isTranslateSupplements?.() === false;

  const setTranslateQuestion = useCallback((value: boolean) => {
    callAction(questionEditorActions.UPDATE_QUESTION_DETAILS, {
      setter: 'setTranslate',
      value
    });
  }, []);

  const setTranslateSupplements = useCallback((value: boolean) => {
    callAction(questionEditorActions.UPDATE_QUESTION_DETAILS, {
      setter: 'setTranslateSupplements',
      value
    });
  }, []);

  const questionIsNew = question && !question.existsOnServer();
  const questionHasChanges =
    questionSet &&
    (!questionSet.getChangeMap().isEmpty() ||
      !question.getChangeMap().isEmpty() ||
      translationsHaveChanges);

  const canTranslateQuestionFields = !subjectQuestionTranslationDisabled && question?.isTranslate();
  const canTranslateSupplements =
    !subjectSupplementTranslationDisabled && question?.isTranslateSupplements();
  const questionSupplementTranslationsEnabled = useMemo(
    () => question?.getAuthoringSupplements().some((s) => s.isTranslate()),
    [question]
  );
  const isTranslationPossible =
    canTranslateQuestionFields ||
    (canTranslateSupplements && questionSupplementTranslationsEnabled);

  return (
    <div className='u-display_flex'>
      <div className='u-width_50pc u-pad_6 edit-page__translation-settings__section-card-border'>
        <div className='u-display_flex u-flex-direction_column u-gap_space-x3'>
          <div className='u-display_flex u-gap_space-x4'>
            <Text size='m' style={{width: '20px'}}>
              <Icon icon={['fal', 'align-left']} />
            </Text>

            <div className='u-display_flex u-justify-content_space-between u-gap_space-x2 u-flex-grow_1'>
              <div className='u-display_flex u-flex-direction_column'>
                <Text size='m'>Question Fields</Text>
                <Text size='s' color='secondary'>
                  Translate title, prompt, answer options, and explanations.
                </Text>
                {subjectQuestionTranslationDisabled && (
                  <Text color='warning' size='s'>
                    Disabled for {subject?.getName()}
                  </Text>
                )}
              </div>

              <Toggle
                disabled={subjectQuestionTranslationDisabled}
                checked={canTranslateQuestionFields}
                onChange={() => {
                  setTranslateQuestion(!question?.isTranslate());
                }}
              />
            </div>
          </div>

          <div className='u-display_flex u-gap_space-x4'>
            <Text size='m' style={{width: '20px'}}>
              <Icon icon={['fal', 'photo-film-music']} />
            </Text>

            <div className='u-display_flex u-justify-content_space-between u-gap_space-x2 u-flex-grow_1'>
              <div className='u-display_flex u-flex-direction_column'>
                <Text size='m'>Supplements</Text>
                <Text size='s' color='secondary'>
                  Translate supplement captions, passages, etc
                </Text>
                {subjectSupplementTranslationDisabled && (
                  <Text color='warning' size='s'>
                    Disabled for {subject?.getName()}
                  </Text>
                )}
              </div>

              <Toggle
                disabled={subjectSupplementTranslationDisabled}
                checked={canTranslateSupplements}
                onChange={() => {
                  setTranslateSupplements(!question?.isTranslateSupplements());
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='u-width_50pc u-pad_6 u-display_flex u-flex-direction_column u-align-items_center u-justify-content_center u-gap_space-x2'>
        {isTranslationPossible && (
          <>
            <GenerateTranslationsButton />

            {questionIsNew || questionHasChanges ? (
              <Text size='m' color='warning'>
                Save changes to generate translations
              </Text>
            ) : (
              <Text size='m'>Or manually enter translations.</Text>
            )}
          </>
        )}
        {!question.isTranslate() &&
          !question.isTranslateSupplements() &&
          !subjectQuestionTranslationDisabled &&
          !subjectSupplementTranslationDisabled && (
            <Text size='m' className='u-text-align_center' color='secondary'>
              Enable question field or supplement translations to get started.
            </Text>
          )}
        {!question.isTranslate() &&
          !subjectQuestionTranslationDisabled &&
          subjectSupplementTranslationDisabled && (
            <Text size='m' className='u-text-align_center' color='secondary'>
              Enable question field translations to get started.
            </Text>
          )}
        {!question.isTranslateSupplements() &&
          subjectQuestionTranslationDisabled &&
          !subjectSupplementTranslationDisabled && (
            <Text size='m' className='u-text-align_center' color='secondary'>
              Enable supplement translations to get started.
            </Text>
          )}
      </div>
    </div>
  );
};
